/**
 * index.js — /obd2-codes. Справочник кодов неисправностей.
 *
 * Человек приходит сюда не с одним кодом, а с тем, что показал сканер: тремя,
 * четырьмя, иногда десятью. Поэтому ввод здесь — не строка поиска, а вставка
 * ЦЕЛОГО списка, и главный ответ выдаётся не по каждому коду отдельно, а по их
 * сочетанию: P0171 в одиночку значит подсос воздуха у своего банка, а P0171
 * вместе с P0174 — общую для обоих банков причину, то есть впуск или
 * расходомер. Ни один онлайн-справочник этого не делает: они отвечают на код,
 * а спрашивают их набором.
 *
 * Второе отличие — код, которого нет в списке, здесь не остаётся без ответа.
 * Пять знаков сами по себе несут структуру: система, стандартный или
 * фирменный, подсистема. «Расшифровки нет» — худший возможный ответ ровно в
 * том случае, ради которого справочник и открыли.
 *
 * Третье — код производителя (вторая цифра 1 или 3) отвечается расшифровкой
 * марки, только когда мы держим её документ (resources/data/obd2-oem, oem.js):
 * с машиной из поля «Машина», с моделями, для которых документ её описывает, и с
 * самим документом. Заявка 1956 (B1499, «мицубиси кольт», 23.10.2026) —
 * docs/why/obd2-codes.md.
 */

import { lookup, combos, structure } from './codes.js';
import { oemMatch, oemShows, ymdParts } from './oem.js';

const SLUG = 'obd2-codes';

let ui = null;
let lastCodes = [];
let lastCyr = 0;

const $ = (id) => document.getElementById(id);
const t = (k, sub) => {
    let s = (ui && ui.lang && ui.lang[k] != null) ? ui.lang[k] : k;
    /* Длинные имена первыми: «:make» иначе съедает начало «:makes». */
    if (sub) for (const p of Object.keys(sub).sort((a, b) => b.length - a.length)) s = String(s).split(':' + p).join(sub[p]);
    return s;
};
/* Воронка. `__stage` сам считает время на шаге и досылает `final_stage`,
   когда человек уходит со страницы, — то есть отвечает на вопрос «где
   отваливаются», а не «сколько нажали». Слаг берётся из <meta name="tool-slug">,
   передавать его не надо. */
const stage = (n, d) => { try { window.__stage && window.__stage(n, d || {}); } catch (e) {} };
const ae = (ev, d) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (e) {} };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* Кириллические двойники латинских букв кода. На русской раскладке «Р0171»
   набирается вместо «P0171» одним непереключённым нажатием и выглядит на
   экране ТОЧНО так же — отличить глазом нельзя. Молчаливый отказ на таком
   вводе неотличим от «такого кода не бывает», и человеку нечего исправлять:
   он видит правильный текст. Поэтому подменяем и ГОВОРИМ, что подменили. */
const CYR_TWIN = { 'Р': 'P', 'В': 'B', 'С': 'C', 'У': 'U', 'А': 'A', 'Е': 'E', 'О': 'O', 'Т': 'T', 'Н': 'H', 'К': 'K', 'М': 'M', 'Х': 'X' };

function normalizeInput(text) {
    let fixed = 0;
    const out = String(text || '').toUpperCase().replace(/[А-ЯЁ]/g, (ch) => {
        const tw = CYR_TWIN[ch];
        if (tw) { fixed++; return tw; }
        return ch;
    });
    return { text: out, fixed: fixed };
}

/**
 * Вытащить коды из чего угодно: списка, скриншота-текста, строки с запятыми.
 *
 * ⚠️ Регистр НЕ значим. Коды переписывают с форумов, из приложений сканеров и
 * из переписки, где они сплошь и рядом строчные; регистрозависимый разбор
 * отвечал «ни одного кода не нашлось» на совершенно правильный ввод.
 */
function parseCodes(text) {
    const out = [];
    const re = /\b([PBCU][0-3][0-9A-F]{3})\b/g;
    let m;
    while ((m = re.exec(normalizeInput(text).text))) {
        const c = m[1];
        if (out.indexOf(c) < 0) out.push(c);
    }
    return out;
}

/**
 * Похоже на код, но кодом не является. Нужен, чтобы отказ называл ПРИЧИНУ:
 * «P9999» отличается от «привет» тем, что человек тут не ошибся местом — он
 * ошибся знаком, и сказать надо именно это.
 */
function nearMisses(text) {
    const out = [];
    const re = /\b([PBCU])([0-9])([0-9A-Z]{3})\b/g;
    let m;
    while ((m = re.exec(normalizeInput(text).text))) {
        if (m[2] >= '0' && m[2] <= '3') continue;          // это настоящий код
        if (out.indexOf(m[0]) < 0) out.push(m[0]);
    }
    return out;
}

const SEV_CLASS = { low: 'ok', mid: 'warn', high: 'bad', stop: 'stop' };

/* ───────────────────── коды производителей (oem.js) ───────────────────── */

/* Машина из поля «Машина» (<x-car-picker id="obCar">) или null, пока модуль
   выбора не поднялся. */
function carNow() {
    const el = ui && ui.car;
    try { return el && el.carPicker ? el.carPicker.value() : null; } catch (e) { return null; }
}

function makeName(id) {
    const m = ui.oem && ui.oem.makes && ui.oem.makes[id];
    return (m && m.name) || String(id || '');
}

/* Дата проверки источника — календарная: собираем её из частей, а не из строки
   (та читается как гринвичская полночь и западнее Гринвича уезжает на день назад). */
function fmtDate(ymd) {
    const p = ymdParts(ymd);
    if (!p) return String(ymd || '');
    try {
        const loc = window.__intlLocale ? window.__intlLocale() : undefined;
        return new Date(p[0], p[1], p[2]).toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return String(ymd); }
}

const has = (k) => !!(ui && ui.lang && ui.lang[k] != null && String(ui.lang[k]).trim() !== '');

const MATCH_NOTE = { exact: 'oem_match_exact', make: 'oem_match_make', model_other: 'oem_match_model_other', only_make: 'oem_match_only_make' };

/**
 * Карточка кода производителя: значение по документу марки, блок, оригинальная
 * формулировка, условия, модели и источники. Без срочности, причин и проверок:
 * их документ либо не даёт, либо даёт для своего прибора, — придумывать их здесь
 * нельзя (docs/why/obd2-codes.md).
 */
function renderOem(code, st, match, car) {
    const mk = makeName(match.makeId);
    const carLabel = car && car.label ? car.label : mk;
    const note = MATCH_NOTE[match.status] ? t(MATCH_NOTE[match.status], { car: carLabel, make: mk }) : '';
    return match.entries.map((e) => {
        const k = 'oem.' + e.key + '.';
        const bits = [];
        bits.push(`<div class="ob-card" data-oem="${esc(match.makeId)}">`);
        bits.push(`<div class="ob-head"><span class="ob-code">${esc(code)}</span>
            <span class="ob-kind">${esc(t('oem_kind', { make: mk }))}</span></div>`);
        bits.push(`<div class="ob-title">${esc(t(k + 'title'))}</div>`);
        bits.push(`<div class="ob-struct">${esc(t('system.' + st.system))} · ${esc(t('oem_unit.' + e.unit))} (<bdi>${esc(e.unit_name)}</bdi>)</div>`);
        if (note) bits.push(`<div class="ob-note">${esc(note)}</div>`);
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${esc(t('oem_original', { make: mk }))}</div>
            <div class="ob-oem-orig"><q lang="${esc(e.lang || 'en')}" dir="ltr">${esc(e.wording)}</q></div></div>`);
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${esc(t('oem_set_label'))}</div><p class="ob-oem-txt">${esc(t(k + 'set'))}</p></div>`);
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${esc(t('oem_manual_label'))}</div><p class="ob-oem-txt">${esc(t(k + 'manual'))}</p></div>`);
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${esc(t('oem_applies_label'))}</div><p class="ob-oem-txt">${esc(t(k + 'applies'))}</p>`
            + (has(k + 'other') ? `<p class="ob-oem-txt">${esc(t(k + 'other'))}</p>` : '') + `</div>`);
        const src = (e.sources || []).slice().sort((a, b) => (a.role === 'contrast') - (b.role === 'contrast'));
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${esc(t('oem_sources_label'))}</div><ol class="ob-oem-src">`
            + src.map((s) => `<li>${s.role === 'contrast' ? `<span class="ob-oem-tag">${esc(t('oem_source_contrast'))}:</span> ` : ''}`
                /* Источник — документ производителя, как он издан: без ссылки. Где мы
                   его читали, на страницу не попадает (OemCodes::payload отдаёт только
                   поля цитаты). Цитата — на языке документа и одним изолированным
                   куском: в арабском интерфейсе иначе точки и тире разлетаются по строке. */
                + `<bdi dir="ltr" lang="${esc(e.lang || 'en')}">${esc(s.publisher)}. ${esc(s.title)}, ${esc(s.edition)}. ${esc(s.section)} — <q>${esc(s.quote)}</q></bdi>`
                + ` · ${esc(t('oem_accessed', { date: fmtDate(s.accessed) }))}</li>`).join('')
            + `</ol></div>`);
        bits.push(`</div>`);
        return bits.join('');
    }).join('');
}

/* Код есть у других марок, но не у выбранной (или марка не выбрана, а марок
   несколько): остаёмся на разборе структуры и называем, у кого он есть. */
function oemMakesLine(match, car) {
    if (!match) return '';
    const makes = match.makes.map(makeName).join(', ');
    /* Выбранной марки в слое нет — её имя берём из выбора машины. */
    const chosen = (car && car.labels && car.labels.make) || makeName(match.makeId);
    if (match.status === 'other_make') return t('oem_other_make', { make: chosen, makes });
    if (match.status === 'ambiguous') return t('oem_ambiguous', { makes });
    return '';
}

function renderOne(entry, match, car) {
    const st = entry.structure;
    const sev = entry.sev;
    const title = entry.tpl ? t('tpl.' + entry.tpl, entry.slots || {}) : t('unknown_title');

    const bits = [];
    bits.push(`<div class="ob-card">`);
    bits.push(`<div class="ob-head">
        <span class="ob-code">${esc(entry.code)}</span>
        ${sev ? `<span class="ob-sev ob-sev-${SEV_CLASS[sev]}">${t('sev.' + sev)}</span>` : ''}
        <span class="ob-kind">${t(st.generic ? 'kind_generic' : 'kind_oem')}</span>
    </div>`);
    bits.push(`<div class="ob-title">${esc(title)}</div>`);

    // Структура — то, что известно про КАЖДЫЙ код, включая незнакомый.
    const struct = [t('system.' + st.system)];
    if (st.subsystem) struct.push(t('sub.' + st.subsystem));
    bits.push(`<div class="ob-struct">${struct.map(esc).join(' · ')}</div>`);

    if (entry.note) bits.push(`<div class="ob-note">${esc(t('note.' + entry.note))}</div>`);

    if (sev) bits.push(`<div class="ob-drive"><b>${t('drive_label')}</b> ${esc(t('drive.' + sev))}</div>`);

    if (entry.causes && entry.causes.length) {
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${t('causes_title')}</div><ol class="ob-list">`
            + entry.causes.map((c) => `<li>${esc(t('cause.' + c))}</li>`).join('')
            + `</ol><div class="ob-hint">${t('causes_hint')}</div></div>`);
    }
    if (entry.checks && entry.checks.length) {
        bits.push(`<div class="ob-sec"><div class="ob-sec-h">${t('checks_title')}</div><ul class="ob-list">`
            + entry.checks.map((c) => `<li>${esc(t('check.' + c))}</li>`).join('')
            + `</ul></div>`);
    }

    if (entry.source === 'structure') {
        const makesLine = oemMakesLine(match, car);
        if (makesLine) bits.push(`<div class="ob-oem-makes">${esc(makesLine)}</div>`);
        bits.push(`<div class="ob-unknown">${t(st.generic ? 'unknown_generic' : 'unknown_oem')}
            <button type="button" class="ob-ask" data-ask="${esc(entry.code)}">${t('btn_ask')}</button></div>`);
    } else if (!st.generic) {
        bits.push(`<div class="ob-note">${t('oem_note')}</div>`);
    }

    bits.push(`</div>`);
    return bits.join('');
}

function render(codes, cyrFixed, opts) {
    const silent = !!(opts && opts.silent);
    lastCodes = codes;
    lastCyr = cyrFixed || 0;
    if (!codes.length) {
        ui.results.innerHTML = '';
        ui.results.hidden = true;
        return;
    }
    ui.results.hidden = false;

    const found = codes.map(lookup).filter(Boolean);
    const cmb = combos(codes);
    /* Код производителя — у какой марки и подходит ли машине (oem.js). Стандартные
       коды (вторая цифра 0 или 2) одинаковы у всех марок, их это не касается. */
    const car = carNow();
    const oem = found.map((f) => (f.structure.generic ? null : oemMatch(f.code, ui.oem, car)));

    let html = '';
    /* Сказать про подменённую раскладку ОБЯЗАТЕЛЬНО: иначе человек не поймёт,
       почему в следующий раз тот же на вид текст сработал или не сработал. */
    if (cyrFixed) html += `<div class="ob-note">${esc(t('cyr_fixed', { n: cyrFixed }))}</div>`;
    if (cmb.length) {
        html += `<div class="ob-combo"><div class="ob-combo-h">🔗 ${t('combo_title')}</div>`;
        for (const c of cmb) {
            html += `<div class="ob-combo-item"><div class="ob-combo-txt">${esc(t('combo.' + c.tpl, { n: c.n || 0 }))}</div>`;
            if (c.causes && c.causes.length) {
                html += `<ol class="ob-list">` + c.causes.map((x) => `<li>${esc(t('cause.' + x))}</li>`).join('') + `</ol>`;
            }
            html += `</div>`;
        }
        html += `<div class="ob-hint">${t('combo_hint')}</div></div>`;
    }

    html += found.map((f, i) => (oemShows(oem[i]) ? renderOem(f.code, f.structure, oem[i], car) : renderOne(f, oem[i], car))).join('');

    const bad = codes.filter((c) => !structure(c));
    if (bad.length) html += `<div class="ob-warn">${t('bad_codes', { codes: bad.join(', ') })}</div>`;

    ui.results.innerHTML = html;
    const sb = $('obShare');
    if (sb) sb.hidden = false;
    /* Смена машины перерисовывает разбор — это не новый запрос, считать его нельзя. */
    if (silent) return;
    /* ⚠️ Пишем ИМЕННО неизвестные коды, а не только их число. Это тот
       единственный сигнал, по которому справочник можно пополнять предметно:
       «каких кодов у нас спрашивают и не находят». Числом этот вопрос не
       отвечается, а спрашивать его будут каждый месяц. Код, на который ответил
       документ производителя, — не неизвестный; что ответил и при какой машине —
       в `oem` («B1499:only_make»). */
    const unknown = found.filter((f, i) => f.source === 'structure' && !oemShows(oem[i])).map((f) => f.code);
    const oemSeen = found.map((f, i) => (oem[i] && oem[i].status !== 'none' ? f.code + ':' + oem[i].status : '')).filter(Boolean);
    stage('lookup', { n: codes.length, unknown: unknown.length, combos: cmb.length });
    ae('feature_use', {
        action: 'lookup', n: codes.length,
        known: found.length - unknown.length,
        combos: cmb.length,
        first: codes[0] || '',
        codes: codes.slice(0, 10).join(','),
        unknown: unknown.slice(0, 10).join(','),
        oem: oemSeen.slice(0, 10).join(','),
        car_make: (car && car.makeId) || '',
        cyr: cyrFixed ? 1 : 0,
    });
}

/* Строка «Марка, модель, год: …» в форме заявки — та машина, что выбрана вверху. */
function renderAskCar() {
    if (!ui.askCarVal) return;
    const v = carValue();
    ui.askCarVal.textContent = v.car || t('ask_car_none');
}

/* ─────────────────────── «пришлите свой код» ───────────────────────────── */

function openAsk(code) {
    ui.askWrap.hidden = false;
    /* Код подставляется сам. Человек только что ввёл его в поле выше и получил
       «расшифровки нет» — просить набрать то же самое второй раз значит
       терять половину заявок на ровном месте. */
    ui.askCode.value = code || (lastCodes[0] || '');
    ui.askText.focus();
    ui.askWrap.scrollIntoView({ block: 'nearest' });
}

async function submitAsk() {
    const code = String(ui.askCode.value || '').trim().toUpperCase();
    const pick = carValue();
    const car = pick.car;
    const text = String(ui.askText.value || '').trim();
    if (!/^[PBCU][0-3][0-9A-F]{3}$/.test(code)) {
        window.__toast && window.__toast(t('ask_bad_code'), 'warning');
        return;
    }
    if (text.length < 3 && car.length < 3) {
        window.__toast && window.__toast(t('ask_need_text'), 'warning');
        return;
    }
    ui.askSend.disabled = true;

    const payload = {
        tool_slug: SLUG,
        // `name` — первичный ключ дедупликации в ToolSuggestion::fileDeduped:
        // повторная отправка того же кода тем же человеком, пока заявка ещё не
        // разобрана, не должна плодить строки в очереди.
        // `car` stays the words of the request (rows before 22.10.2026 have only it);
        // the catalogue ids come along when the car was picked from the list.
        data: Object.assign({ name: code, car, meaning: text }, pick.ids),
        locale: window.__LOCALE || 'en',
    };

    const token = (document.querySelector('meta[name="csrf-token"]') || {}).content || '';
    const send = (tk) => fetch('/api/tool-suggestion', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': tk },
        body: JSON.stringify(payload),
    });

    try {
        let r = await send(token);
        if (r.status === 419 && window.__refreshCsrf) r = await send(await window.__refreshCsrf());
        if (r.ok) {
            ui.askForm.hidden = true;
            ui.askDone.hidden = false;
            ae('feature_use', { action: 'suggest_submitted', code });
        } else {
            ui.askSend.disabled = false;
            window.__toast && window.__toast(t(r.status === 429 ? 'ask_rate_limit' : 'ask_failed'), 'error');
            ae('tool_error', { action: 'suggest_failed', status: r.status });
        }
    } catch (e) {
        ui.askSend.disabled = false;
        window.__toast && window.__toast(t('ask_failed'), 'error');
    }
}

/* Машина из общего справочника (<x-car-picker id="obCar">, docs/why/car-catalog.md) —
   одна на страницу: она решает, чья расшифровка у кода производителя, и она же уходит
   в заявку. Не поднялся модуль выбора — остаётся его поле ввода, и машина уходит
   текстом, как до справочника. */
function carValue() {
    const el = ui.car;
    const v = el && el.carPicker ? el.carPicker.value() : null;
    if (!v) {
        const q = document.getElementById('obCar-q');
        return { car: String((q && q.value) || '').trim().slice(0, 120), ids: {} };
    }
    const ids = {};
    if (v.makeId) ids.car_make = v.makeId;
    if (v.modelId) ids.car_model = v.modelId;
    if (v.generationId) ids.car_generation = v.generationId;
    if (v.engineId) ids.car_engine = v.engineId;
    if (v.year) ids.car_year = String(v.year);
    return { car: String(v.modelId ? v.label : v.text || '').trim().slice(0, 120), ids };
}

/* ──────────────────────────────── монтаж ───────────────────────────────── */

export function mount(cfg) {
    ui = {
        cfg, lang: cfg.lang || {}, oem: cfg.oem || { makes: {} },
        input: $('obInput'), go: $('obGo'), results: $('obResults'), car: $('obCar'),
        askWrap: $('obAskWrap'), askForm: $('obAskForm'), askDone: $('obAskDone'),
        askCode: $('obAskCode'), askCarVal: $('obAskCarVal'), askText: $('obAskText'), askSend: $('obAskSend'),
    };

    /* Машину выбрали или сменили — разбор, который уже на экране, отвечает за
       новую машину (код производителя у другой марки значит другое), а строка
       заявки называет её. Событие всплывает от корня выбора. */
    if (ui.car) {
        const onCar = () => {
            renderAskCar();
            if (lastCodes.length) render(lastCodes, lastCyr, { silent: true });
        };
        ui.car.addEventListener('car-picker:change', onCar);
        ui.car.addEventListener('car-picker:ready', renderAskCar);
    }
    renderAskCar();

    const run = () => {
        const raw = ui.input.value;
        const norm = normalizeInput(raw);
        const codes = parseCodes(raw);
        if (!codes.length) {
            /* ⚠️ Раньше здесь стоял `return` — и на экране ОСТАВАЛСЯ разбор
               ПРЕДЫДУЩЕГО кода. Тост уходит через несколько секунд, а чужой
               диагноз остаётся: человек вставил новые коды, промахнулся и
               читает «эффективность катализатора ниже порога» от прошлого
               запроса. В справочнике неисправностей это худший из возможных
               отказов, потому что выглядит как ответ. */
            const near = nearMisses(raw);
            ui.results.hidden = false;
            ui.results.innerHTML = '<div class="ob-warn">'
                + esc(near.length ? t('near_miss', { codes: near.join(', ') }) : t('no_codes'))
                + '</div>';
            lastCodes = [];
            const sb0 = $('obShare');
            if (sb0) sb0.hidden = true;
            window.__toast && window.__toast(t('no_codes'), 'warning');
            stage('no_codes', { near: near.length });
            ae('feature_use', { action: 'no_codes', near: near.join(','), len: String(raw || '').length });
            return;
        }
        render(codes, norm.fixed);
    };

    ui.go.addEventListener('click', run);
    ui.input.addEventListener('keydown', (e) => {
        // Ctrl+Enter, а не Enter: поле многострочное, коды вставляют списком.
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); run(); }
    });

    ui.results.addEventListener('click', (e) => {
        const c = e.target && e.target.dataset && e.target.dataset.ask;
        if (c) openAsk(c);
    });
    const openBtn = $('obAskOpen');
    if (openBtn) openBtn.addEventListener('click', () => openAsk(''));
    ui.askSend.addEventListener('click', submitAsk);

    // Быстрые примеры — не украшение: они показывают, ЧТО можно вставить,
    // включая набор из нескольких кодов, который и есть основной случай.
    document.querySelectorAll('[data-sample]').forEach((el) => {
        el.addEventListener('click', () => {
            ui.input.value = el.dataset.sample;
            run();
        });
    });

    /* Ссылка на разбор. `?code=` (один код) остаётся ради уже разосланных
       ссылок, `?codes=` несёт весь набор — а набор здесь и есть главное:
       разбор СОЧЕТАНИЯ отличает этот справочник от остальных, и уносят с
       собой именно его. */
    try {
        const sp = new URLSearchParams(location.search);
        const q = sp.get('codes') || sp.get('code');
        if (q) {
            ui.input.value = q.replace(/[,;+]+/g, ' ').trim();
            const codes = parseCodes(ui.input.value);
            if (codes.length) render(codes);
        }
    } catch (e) {}

    /* Кнопка «скопировать ссылку». Появляется только когда есть что уносить,
       и кладёт в буфер адрес С кодами, а не текущий — человек чаще всего
       пришёл сюда сам и в адресной строке у него голый /obd2-codes. */
    const shareBtn = $('obShare');
    if (shareBtn) {
        shareBtn.addEventListener('click', async () => {
            if (!lastCodes.length) return;
            const url = location.origin + location.pathname + '?codes=' + lastCodes.join(',');
            let done = false;
            try { await navigator.clipboard.writeText(url); done = true; } catch (e) {}
            if (!done) {
                /* Буфер закрыт (нет разрешения, небезопасный контекст, старый
                   webview) — показываем адрес, чтобы его можно было выделить
                   руками. Молча ничего не делать нельзя: нажатие было. */
                ui.input.value = url;
                ui.input.select();
            }
            window.__toast && window.__toast(t(done ? 'share_copied' : 'share_manual'), done ? 'success' : 'info');
            ae('feature_use', { action: 'share', n: lastCodes.length, ok: done ? 1 : 0 });
        });
    }
}

function boot() {
    const cfg = window.__OBD2_CFG;
    if (!cfg || !document.getElementById('obInput')) return;
    try { mount(cfg); } catch (e) {
        window.__handleError ? window.__handleError(e, SLUG) : console.error(e);
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
